import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Droplets, LogOut, Download, Search, RefreshCw, CheckCircle2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const token = localStorage.getItem("hw_admin_token");
  const email = localStorage.getItem("hw_admin_email");

  const logout = useCallback(() => {
    localStorage.removeItem("hw_admin_token");
    localStorage.removeItem("hw_admin_email");
    navigate("/admin/login");
  }, [navigate]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } });
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    fetchLeads();
  }, [token, navigate, fetchLeads]);

  const filtered = useMemo(() => {
    if (!query.trim()) return leads;
    const q = query.toLowerCase();
    return leads.filter((l) =>
      [l.firstName, l.lastName, l.email, l.phone, l.address, l.waterSource]
        .filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [leads, query]);

  const exportCSV = () => {
    const cols = ["created_at", "firstName", "lastName", "phone", "email", "address", "waterSource", "smsConsent", "marketingConsent", "termsAgreed", "source", "ghl_synced", "ghl_contact_id"];
    const header = cols.join(",");
    const rows = filtered.map((l) =>
      cols.map((c) => `"${String(l[c] ?? "").replace(/"/g, '""')}"`).join(",")
    );
    const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `honest-water-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#0f4c81" }}>
              <Droplets className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-extrabold text-[#0f4c81] text-lg">Leads Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-slate-500">{email}</span>
            <button onClick={logout} data-testid="admin-logout-button"
              className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-[#0f4c81]">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading font-extrabold text-2xl text-[#0f4c81]">Quote Requests</h1>
            <p className="text-slate-500 text-sm mt-1" data-testid="leads-count">{filtered.length} lead{filtered.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="leads-search" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search leads..." className="hw-input !py-2 !pl-9 w-56" />
            </div>
            <button onClick={fetchLeads} data-testid="leads-refresh" className="btn-outline !py-2 !px-4 !text-sm">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button onClick={exportCSV} data-testid="leads-export" className="btn-cta !py-2 !px-4 !text-sm">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-slate-500 font-heading">
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Phone</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Address</th>
                  <th className="px-4 py-3 font-semibold">Water</th>
                  <th className="px-4 py-3 font-semibold">SMS</th>
                  <th className="px-4 py-3 font-semibold">GHL</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400" data-testid="leads-empty">No leads yet. Submissions will appear here.</td></tr>
                ) : (
                  filtered.map((l, i) => (
                    <tr key={l.id || i} data-testid={`lead-row-${i}`} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                      <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">{l.firstName} {l.lastName}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.phone}</td>
                      <td className="px-4 py-3 text-slate-600">{l.email}</td>
                      <td className="px-4 py-3 text-slate-600">{l.address || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.waterSource || "—"}</td>
                      <td className="px-4 py-3">{l.smsConsent ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-slate-300" />}</td>
                      <td className="px-4 py-3">
                        {l.ghl_synced
                          ? <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">Synced</span>
                          : <span title={l.ghl_error || "Saved locally (GHL not synced)"}
                              className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded-full cursor-help">
                              {l.ghl_error ? "Not synced ⚠" : "Local"}
                            </span>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
